// ============================================
// Multi-export comparison tracks
// ============================================
//
// Turns decoded export records (utils/wearableExport) into render-ready
// "tracks" for the compare timeline: per-band sample segments placed on an
// absolute time axis, a min/max envelope pyramid for fast canvas drawing at
// any zoom, and impact markers. Pure functions — the canvas component stays
// dumb.

export const MAX_COMPARE_TRACKS = 10;

export const TRACK_COLORS = [
  "#E07A3A",
  "#2563EB",
  "#16A34A",
  "#DC2626",
  "#7C3AED",
  "#0D9488",
  "#D97706",
  "#DB2777",
  "#4B5563",
  "#0EA5E9",
];

const BUCKET = 16; // envelope bucket size (samples) — coarse level for zoom-out

// One contiguous stretch of samples: a record's band data at `hz`, starting
// at absolute `startMs`. `mags`/`xs`/`ys`/`zs` are Float32Arrays in g.
function buildSegment(record, band) {
  const key = band === "high" ? "highG" : "lowG";
  const hzKey = band === "high" ? "highgHz" : "lowgHz";
  const samples = record[key];
  if (!Array.isArray(samples) || samples.length === 0) return null;
  const hz = record[hzKey] || 960;
  const n = samples.length;
  const mags = new Float32Array(n);
  const xs = new Float32Array(n);
  const ys = new Float32Array(n);
  const zs = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const s = samples[i];
    mags[i] = s.mag;
    xs[i] = s.x;
    ys[i] = s.y;
    zs[i] = s.z;
  }
  // Envelope pyramid, one coarse level: min/max per BUCKET samples.
  const nb = Math.ceil(n / BUCKET);
  const bMin = new Float32Array(nb);
  const bMax = new Float32Array(nb);
  for (let b = 0; b < nb; b++) {
    let mn = Infinity;
    let mx = -Infinity;
    const end = Math.min(n, (b + 1) * BUCKET);
    for (let i = b * BUCKET; i < end; i++) {
      const v = mags[i];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    bMin[b] = mn;
    bMax[b] = mx;
  }
  const startMs =
    record.windowStartMs != null ? record.windowStartMs : record.timestampMs;
  return {
    startMs,
    hz,
    n,
    isSnippet: !!record.isSnippet,
    durMs: (n / hz) * 1000,
    mags,
    xs,
    ys,
    zs,
    bMin,
    bMax,
    bucket: BUCKET,
  };
}

/**
 * Build one comparison track from a decoded export.
 * Returns { segments: {low, high}, impacts, extent: {t0,t1} | null,
 * epochTimed, peakG, recordCount }.
 */
export function buildTrack(records) {
  const valid = (records || []).filter((r) => r && r.isValid !== false);
  const segments = { low: [], high: [] };
  const impacts = [];
  let epochTimed = valid.length > 0;
  let peakG = 0;
  let impactCount = 0;
  let snippetCount = 0;
  for (const r of valid) {
    if (r.isMarker) continue; // session boundaries carry no waveform
    if (!r.timestampIsEpoch) epochTimed = false;
    if (r.isSnippet) snippetCount++;
    else impactCount++;
    for (const band of ["low", "high"]) {
      const seg = buildSegment(r, band);
      if (seg) segments[band].push(seg);
    }
    if (typeof r.peakMagG === "number") {
      if (r.peakMagG > peakG) peakG = r.peakMagG;
      impacts.push({
        tMs: r.peakTimeMs ?? r.timestampMs,
        peakG: r.peakMagG,
        band: r.band || null,
        impactId: r.impactId ?? null,
        isSnippet: !!r.isSnippet,
      });
    }
  }
  segments.low.sort((a, b) => a.startMs - b.startMs);
  segments.high.sort((a, b) => a.startMs - b.startMs);
  impacts.sort((a, b) => a.tMs - b.tMs);

  let extent = null;
  for (const band of ["low", "high"]) {
    for (const s of segments[band]) {
      const t1 = s.startMs + s.durMs;
      if (!extent) extent = { t0: s.startMs, t1 };
      else {
        if (s.startMs < extent.t0) extent.t0 = s.startMs;
        if (t1 > extent.t1) extent.t1 = t1;
      }
    }
  }
  return {
    segments,
    impacts,
    extent,
    epochTimed,
    peakG,
    recordCount: valid.length,
    impactCount,
    snippetCount,
  };
}

/**
 * Sample lookup: the track's value at absolute time `tMs` (track offset
 * already applied by the caller) for the given band. Returns
 * { mag, x, y, z, tSampleMs } or null when no record covers that instant.
 */
export function valueAt(track, band, tMs) {
  const segs = track.segments[band === "high" ? "high" : "low"];
  // Binary search the last segment starting at or before tMs.
  let lo = 0;
  let hi = segs.length - 1;
  let idx = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (segs[mid].startMs <= tMs) {
      idx = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (idx === -1) return null;
  const seg = segs[idx];
  if (tMs > seg.startMs + seg.durMs) return null;
  const i = Math.min(
    seg.n - 1,
    Math.max(0, Math.round(((tMs - seg.startMs) / 1000) * seg.hz))
  );
  return {
    mag: seg.mags[i],
    x: seg.xs[i],
    y: seg.ys[i],
    z: seg.zs[i],
    tSampleMs: seg.startMs + (i / seg.hz) * 1000,
  };
}

/**
 * Envelope of [t0Ms, t1Ms) for one segment: { min, max } or null if no
 * overlap. Uses the bucket pyramid when the span is coarse, raw samples when
 * fine — this is the per-pixel-column workhorse for the canvas.
 */
export function segmentEnvelope(seg, t0Ms, t1Ms) {
  const i0 = Math.max(0, Math.floor(((t0Ms - seg.startMs) / 1000) * seg.hz));
  const i1 = Math.min(seg.n, Math.ceil(((t1Ms - seg.startMs) / 1000) * seg.hz));
  if (i1 <= i0) return null;
  let mn = Infinity;
  let mx = -Infinity;
  if (i1 - i0 >= seg.bucket * 2) {
    // Coarse: whole buckets in the middle, raw samples at the ragged edges.
    const b0 = Math.ceil(i0 / seg.bucket);
    const b1 = Math.floor(i1 / seg.bucket);
    for (let b = b0; b < b1; b++) {
      if (seg.bMin[b] < mn) mn = seg.bMin[b];
      if (seg.bMax[b] > mx) mx = seg.bMax[b];
    }
    for (let i = i0; i < Math.min(i1, b0 * seg.bucket); i++) {
      const v = seg.mags[i];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    for (let i = Math.max(i0, b1 * seg.bucket); i < i1; i++) {
      const v = seg.mags[i];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
  } else {
    for (let i = i0; i < i1; i++) {
      const v = seg.mags[i];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
  }
  if (mn === Infinity) return null;
  return { min: mn, max: mx };
}

/** Overall extent of many tracks with their per-track offsets applied. */
export function tracksExtent(tracks) {
  let t0 = Infinity;
  let t1 = -Infinity;
  for (const tr of tracks) {
    if (!tr.data.extent) continue;
    const off = tr.offsetMs || 0;
    if (tr.data.extent.t0 + off < t0) t0 = tr.data.extent.t0 + off;
    if (tr.data.extent.t1 + off > t1) t1 = tr.data.extent.t1 + off;
  }
  return t0 < t1 ? { t0, t1 } : null;
}

/**
 * Offsets that make every track's first sample coincide with the earliest
 * track's — the manual-alignment starting point for unsynced clocks.
 */
export function alignStartOffsets(tracks) {
  const starts = tracks.map((tr) => tr.data.extent?.t0 ?? null);
  const base = Math.min(...starts.filter((s) => s != null));
  return tracks.map((tr, i) =>
    starts[i] == null ? tr.offsetMs || 0 : base - starts[i]
  );
}
